import {
  DispatchType,
  type DispatchesInfo,
  type DispatchInfo,
} from '../../../types/businessTypes';

export const DISPATCH_TYPE_ORDER: readonly DispatchType[] = [
  DispatchType.PICKUP,
  DispatchType.DELIVERY,
  DispatchType.DINE_IN,
];

/**
 * Whether a configured dispatch type is selectable in the picker.
 *
 * Pickup / Dine-in are address-independent, so we gate on the authoritative
 * first-available slot returned by the backend: a missing slot means the
 * restaurant isn't open for that method right now.
 *
 * Delivery is always selectable when configured. Backend slot lookup needs
 * an address, and the address we have (if any) comes from the cart — it
 * may be empty, stale, or out-of-zone, so the slot signal is unreliable
 * here. The FulfillmentMethod lightbox enforces validity on Save, so
 * leaving the picker option clickable lets the customer enter / correct
 * an address.
 */
export const isDispatchTypeSelectable = (
  type: DispatchType,
  dispatchInfo: DispatchInfo | undefined,
): boolean => {
  if (!dispatchInfo) {
    return false;
  }

  if (type === DispatchType.DELIVERY) {
    return true;
  }

  return !!dispatchInfo.selectedTimeSlot;
};

/**
 * Decides which dispatch type should be selected on initial load.
 */
export const resolveSelectedDispatchType = ({
  defaultDispatchType,
  configuredDispatchTypes,
  dispatchesInfo,
}: {
  defaultDispatchType?: DispatchType;
  configuredDispatchTypes: DispatchType[];
  dispatchesInfo: Partial<DispatchesInfo>;
}): DispatchType => {
  if (configuredDispatchTypes.length === 1) {
    return configuredDispatchTypes[0];
  }

  const isAvailable = (type: DispatchType) =>
    !!dispatchesInfo[type]?.selectedTimeSlot;
  const isConfigured = (type: DispatchType) =>
    configuredDispatchTypes.includes(type);

  // When no method has a bookable slot (backend hasn't returned anything
  // yet, or the restaurant is closed for all configured methods), configured
  // methods still count as candidates so the picker doesn't land in a
  // "nothing selected" state.
  const noDispatchesAvailable = !DISPATCH_TYPE_ORDER.some(isAvailable);
  const shouldDefaultTo = (type: DispatchType) =>
    defaultDispatchType === type &&
    (isAvailable(type) || (noDispatchesAvailable && isConfigured(type)));

  if (shouldDefaultTo(DispatchType.DINE_IN)) {
    return DispatchType.DINE_IN;
  }

  // Delivery wins when pickup isn't the operation's default — but only if
  // delivery actually has a slot. There's no `noDispatchesAvailable`
  // fallback for delivery: it would dead-end without an address.
  if (
    isAvailable(DispatchType.DELIVERY) &&
    !shouldDefaultTo(DispatchType.PICKUP)
  ) {
    return DispatchType.DELIVERY;
  }

  return DispatchType.PICKUP;
};
